import { useGetHeliosFilesListQuery } from 'store/services/helios/files';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { Typography as MuiTypography } from '@mui/material';
import { FileWrapper } from './File.styles';
import { useTranslation } from 'react-i18next';

import { Loader } from 'components/common';
import { getBaseUrl } from 'utils/baseUrl';
import { FileItem } from 'components/user/DummyHelios/Uploader/FileItem';
import { FilePreviewPopover } from '../FileUpload/components/FilePreviewPopover';
import { useFilePreviewPopover } from '../FileUpload/hooks/useFilePreviewPopover';

import type { HeliosRecordDataTableColumnSettings } from 'types/dataHelios';
import type { HeliosCellData } from 'types/helios/agendaListing';

interface Props {
  value: string;
  dataType?: string;
  settings?: HeliosRecordDataTableColumnSettings;
  templateItemId?: number;
  templateViewId?: string;
  data?: HeliosCellData;
}

const FileBase = ({ value, settings, templateItemId, templateViewId, data }: Props) => {
  const { t } = useTranslation();
  const isEmptyValue = value === '';
  const isSingleFile = !settings?.externalFile.dynamicRelationId;
  const filesCount = isSingleFile ? (isEmptyValue ? 0 : 1) : (data?.dynamicRelationValuesCount ?? 0);

  const { open, anchorEl, handleMouseEnter, handleClick, scheduleClose, handlePopoverMouseEnter, closePopover } = useFilePreviewPopover({
    filesCount,
  });

  const {
    data: files,
    isFetching,
    refetch,
  } = useGetHeliosFilesListQuery(
    {
      templateId: templateViewId,
      templateItemId,
      templateItemValue: typeof data?.rawValue === 'string' ? data.rawValue : '',
      recordId: data?.recordNumber != null ? String(data.recordNumber) : '',
    },
    {
      skip: !open || isSingleFile || filesCount === 0,
    }
  );

  useEffect(() => {
    if (!open || isSingleFile || filesCount === 0) return;
    void refetch();
  }, [open, isSingleFile, filesCount, refetch]);

  const singleFile = useMemo(() => {
    if (typeof data?.rawValue === 'object') {
      return {
        fileId: data.rawValue?.id ?? 0,
        name: data.rawValue?.name ?? '',
        url: getBaseUrl(`/api/helios/Files/DownloadFile?templateId=${templateViewId}&fileId=${data.rawValue?.id}`),
      };
    }
  }, [templateViewId, data?.rawValue]);

  const fileContent = useCallback(() => {
    if (filesCount === 0) return <MuiTypography>{t('common.noFiles')}</MuiTypography>;
    if (singleFile) return <FileItem disabledRemove={true} file={singleFile} />;
    if (isFetching) return <Loader />;
    return files?.map((file) => (
      <FileItem
        disabledRemove={true}
        key={file.id}
        file={{
          fileId: file?.id,
          name: file?.name,
          extension: file?.extension,
          url: getBaseUrl(`/api/helios/Files/DownloadFile?templateId=${templateViewId}&fileId=${file.id}`),
        }}
      />
    ));
  }, [filesCount, t, singleFile, isFetching, files, templateViewId]);

  return (
    <FilePreviewPopover
      label={`${t('common.count')}: ${filesCount}`}
      open={open}
      anchorEl={anchorEl}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={scheduleClose}
      onClick={handleClick}
      onPopoverMouseEnter={handlePopoverMouseEnter}
      onClose={closePopover}>
      <FileWrapper>{fileContent()}</FileWrapper>
    </FilePreviewPopover>
  );
};

export const FileHelios = memo(FileBase) as typeof FileBase;
